import { tx as translateInterfaceText } from '@/utils/i18n';
import DOMPurify from 'dompurify';

/**
 * Security utility functions for safe data handling
 */

/**
 * Sanitize HTML content using DOMPurify to prevent XSS attacks
 */
export function sanitizeHTML(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Configure DOMPurify with allowlist approach
  const config = {
    ALLOWED_TAGS: ['button', 'div', 'span', 'a', 'p', 'br', 'strong', 'em', 'i', 'b'],
    ALLOWED_ATTR: ['class', 'style', 'href', 'target', 'id'],
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_DOM_IMPORT: false,
    SANITIZE_DOM: true,
    KEEP_CONTENT: false,
    IN_PLACE: false,
    USE_PROFILES: { html: true },
    // Sem FORCE_BODY o DOMPurify DESCARTA o bloco <style> (ao parsear o fragmento
    // o <style> cai em contexto de <head> e é removido), deixando o HTML do botão
    // de ação sem CSS no front. Forçar o parse dentro do <body> preserva o
    // <style>/CSS inline. Scripts, handlers on*, javascript: e <iframe> continuam
    // sendo removidos normalmente pelo profile html.
    FORCE_BODY: true
  };

  try {
    return DOMPurify.sanitize(html, config);
  } catch (error) {
    console.error('HTML sanitization failed:', error);
    return '';
  }
}

/**
 * Validate and sanitize localStorage data
 */
export function secureLocalStorageGet(key: string): any {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;
    
    // Parse and validate JSON
    const parsed = JSON.parse(item);
    
    // Basic integrity check - ensure the data structure is expected
    if (typeof parsed === 'object' && parsed !== null) {
      return parsed;
    }
    
    return parsed;
  } catch (error) {
    console.error(`Failed to securely read localStorage key "${key}":`, error);
    // Clean up corrupted data
    localStorage.removeItem(key);
    return null;
  }
}

/**
 * Securely set localStorage data with validation
 */
export function secureLocalStorageSet(key: string, value: any): boolean {
  try {
    // Validate key
    if (!key || typeof key !== 'string' || key.length > 200) {
      console.error('Invalid localStorage key');
      return false;
    }

    // Validate and stringify value
    const serialized = JSON.stringify(value);
    
    // Check size limit (5MB typical limit)
    if (serialized.length > 5 * 1024 * 1024) {
      console.error('localStorage value too large');
      return false;
    }

    localStorage.setItem(key, serialized);
    return true;
  } catch (error) {
    console.error(`Failed to securely set localStorage key "${key}":`, error);
    return false;
  }
}

/**
 * Validate HTML input to prevent malicious content
 */
export function validateHtmlInput(html: string): { isValid: boolean; error?: string } {
  if (!html || typeof html !== 'string') {
    return { isValid: false, error: translateInterfaceText("HTML input must be a non-empty string") };
  }

  // Check length limit
  if (html.length > 10000) {
    return { isValid: false, error: translateInterfaceText("HTML content too long (max 10,000 characters)") };
  }

  // Check for dangerous patterns
  const dangerousPatterns = [
    /<script/i,
    /javascript:/i,
    /vbscript:/i,
    /on\w+\s*=/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /<form/i,
    /<meta/i,
    /<link/i
  ];

  for (const pattern of dangerousPatterns) {
    if (pattern.test(html)) {
      return { isValid: false, error: translateInterfaceText("HTML contains potentially dangerous content") };
    }
  }

  return { isValid: true };
}

/**
 * Validate URL to prevent malicious redirects
 */
export function validateUrl(url: string): { isValid: boolean; error?: string } {
  if (!url || typeof url !== 'string') {
    return { isValid: false, error: translateInterfaceText("URL must be a non-empty string") };
  }

  // Check length
  if (url.length > 2000) {
    return { isValid: false, error: translateInterfaceText("URL too long") };
  }

  try {
    const parsedUrl = new URL(url);
    
    // Only allow safe protocols
    const allowedProtocols = ['http:', 'https:', 'mailto:', 'tel:'];
    if (!allowedProtocols.includes(parsedUrl.protocol)) {
      return { isValid: false, error: translateInterfaceText("Invalid URL protocol") };
    }

    // Check for suspicious patterns
    if (parsedUrl.hostname.includes('..') || parsedUrl.pathname.includes('..')) {
      return { isValid: false, error: translateInterfaceText("URL contains suspicious patterns") };
    }

    return { isValid: true };
  } catch (error) {
    return { isValid: false, error: translateInterfaceText("Invalid URL format") };
  }
}

/**
 * Rate limiting helper
 */
class RateLimiter {
  private attempts: Map<string, number[]> = new Map();
  private readonly maxAttempts: number;
  private readonly windowMs: number;

  constructor(maxAttempts = 10, windowMs = 60000) {
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
  }

  isAllowed(key: string): boolean {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    
    // Clean old attempts
    const validAttempts = attempts.filter(time => now - time < this.windowMs);
    
    if (validAttempts.length >= this.maxAttempts) {
      return false;
    }

    validAttempts.push(now);
    this.attempts.set(key, validAttempts);
    return true;
  }
}

export const globalRateLimiter = new RateLimiter();